import { useEffect, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useUpdateTopViewContext } from '../../view-atoms'
import { useStagedAction } from '../../hooks/use-staged-action'
import {
  isEditableActionStatus,
  useStagedActionStatus,
  type ActionStatus,
  type StagedAction,
} from '../../hooks/use-staged-actions'
import { stagedActionEditStatesAtom, type ActionEditState } from '../../atoms'
import { requiresExplicitSave, type ActionInlineDraft } from './inline-drafts'
import { actionInlineDraftsAtom } from './inline-draft-state'
import { deriveActionViewState, type ActionViewState } from './action-view-state'

export interface ActionViewContext {
  actionId: string
  title?: string
  /**
   * Set when opening a pending AskUserQuestion / AskUserFeedback — drives the
   * header framing. Kept distinct rather than collapsed to one "answerable"
   * value: the breadcrumb has to read "Feedback", not "Question", or the row
   * promises something is blocked on the user when nothing is.
   */
  kind?: 'question' | 'feedback'
  /** Land with the caret at the end of the body editor — set by the hover card's edit affordance. */
  focusContent?: boolean
  /**
   * The action's chat drawer is open (HIG-5977): the scoped edit thread slides
   * up over the view as a bottom sheet. Flipped in place on the open entry —
   * set when a scoped edit is submitted, cleared when the sheet is dismissed
   * (scrim click, swipe-down, Escape).
   */
  chat?: boolean
  /** Dev-only Braintrust permalink, carried from the History row that opened this view. */
  braintrustLink?: string
}

export interface ActionViewModel {
  action: StagedAction | undefined
  isLoading: boolean
  error: boolean
  state: ActionViewState
  status: ActionStatus
  isInvoking: boolean
  editState: ActionEditState | undefined
  /**
   * The action's edit-thread conversation, derived once for every surface
   * that touches it (the drawer's mount, the busy pill's jump back in). The
   * server row is the source of truth; the in-memory edit state covers the
   * refetch window right after a submit stamps one. Undefined for a
   * conversation-less draft (the legacy edit endpoint) — no thread exists.
   */
  chatId: string | undefined
  /** Unsent inline edits (HIG-5830), overlaying the server draft until saved. */
  draft: ActionInlineDraft | undefined
  /**
   * True while a staged action owes an explicit Save — drives the Save footer +
   * green glow. Body edits only: a title edit saves itself on blur, so it never
   * takes the footer away from Execute (unless its own commit failed).
   */
  isDirty: boolean
  /**
   * The draft is still the user's to change (`isEditableActionStatus`) — the
   * one gate every edit surface in this view reads: inline title, inline body,
   * and the CTA's pre-invoke title flush.
   */
  isEditable: boolean
}

/** Resolves the action and derives which view state to render. */
export function useActionViewModel(actionId: string | undefined): ActionViewModel {
  const { action, isLoading, error } = useStagedAction({ actionId })
  // The subject only needs an id; the placeholder keeps hook order stable while
  // the action loads (its derived status is discarded until `action` resolves).
  const { status, isInvoking } = useStagedActionStatus(action ?? { id: actionId ?? '' })
  const editStateAtom = useMemo(
    () => selectAtom(stagedActionEditStatesAtom, (states) => (actionId ? states[actionId] : undefined)),
    [actionId],
  )
  const editState = useAtomValue(editStateAtom)
  const draftAtom = useMemo(
    () => selectAtom(actionInlineDraftsAtom, (drafts) => (actionId ? drafts[actionId] : undefined)),
    [actionId],
  )
  const draft = useAtomValue(draftAtom)
  const isEditable = isEditableActionStatus(status)
  const isDirty = isEditable && requiresExplicitSave(draft)
  const state = action ? deriveActionViewState({ action, status, editState }) : 'ready'

  return useMemo(
    () => ({
      action: action ?? undefined,
      isLoading,
      error: !!error,
      status,
      isInvoking,
      editState,
      chatId: action?.conversationId ?? editState?.conversationId ?? undefined,
      draft,
      isEditable,
      // A stale draft on an executed/archived action must not resurrect the Save
      // footer — but a failed one still owns its draft, and fixing it by hand is
      // the other half of Retry.
      isDirty,
      state,
    }),
    [action, draft, editState, error, isDirty, isEditable, isInvoking, isLoading, state, status],
  )
}

/**
 * Latch `kind` onto the open entry once the action resolves.
 *
 * The registry's label reads from context, but most callers open the view with
 * an id alone — they have no way to know it's a question or a feedback card.
 * The view does, so it writes the answer back and the breadcrumb stops calling
 * either one an Action.
 */
export function useLatchQuestionKind(context: ActionViewContext | undefined, state: ActionViewState) {
  const updateViewContext = useUpdateTopViewContext('action')
  const kind = state === 'question' ? 'question' : state === 'feedback' ? 'feedback' : undefined
  const alreadyLatched = context?.kind === kind

  useEffect(() => {
    if (!kind || alreadyLatched) return
    updateViewContext((current) => (current ? { ...current, kind } : current))
  }, [kind, alreadyLatched, updateViewContext])
}
